import type { CustomChartSerie } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { dequal } from "dequal"
import { atom } from "jotai"
import { atomFamily } from "jotai/utils"
import { suspend } from "../../../../utils/jotai/suspend"
import { unwrapWithData } from "../../../../utils/jotai/unwrap"
import { toDictionary } from "../../../../utils/toDictionary"
import type { MetricConfiguration } from "../../../../utils/trpc"
import type { TooltipOptions } from "../../tt-ui-chart/Tooltip"
import { metricsConfigurationsAtom } from "./meta-atoms"

const medianAggregateMetrics = new Set([
  "transaction-fee-average",
  "block-time",
  "arpu",
  "afpu",
  "trading-volume-avg-per-trade-count",
  "trading-volume-avg-per-user",
])

function isAggregateMetric(metricConfig: MetricConfiguration) {
  return metricConfig.metric_id !== "price" && metricConfig.format !== "ratio"
}

export const isMedianMetric = (metricConfig: MetricConfiguration) => {
  return (
    metricConfig.format === "ratio" ||
    medianAggregateMetrics.has(metricConfig.slug)
  )
}

export const getTooltipOptionsFromChartSettingsAtomFamily = atomFamily(
  (chartSettings: Array<CustomChartSerie>) => {
    const tooltipOptionsFromChartSettingsAtom = atom((get) => {
      const chartSetting = chartSettings[0]
      if (chartSettings.length !== 1 || !chartSetting) {
        return undefined
      }

      const metricConfigurationsDictionary = toDictionary(
        unwrapWithData(suspend(get(metricsConfigurationsAtom))),
        "slug"
      )

      const metricConfig = metricConfigurationsDictionary.get(
        chartSetting.metric
      )
      if (!metricConfig) {
        return undefined
      }

      // Percent-format metrics (e.g. asset_apy) can't be summed across assets;
      // show median instead so the bottom row stays informative.
      const isPercentMetric = metricConfig.format === "percent"

      const tooltipOpts: TooltipOptions = {
        sort: true,
        showTotal:
          isPercentMetric ||
          (isAggregateMetric(metricConfig) &&
            !isMedianMetric(metricConfig) &&
            chartSetting.display !== "percentage" &&
            !chartSetting.ratioMetric), // Hide total for ratio charts
        aggregation: isPercentMetric ? "median" : "sum",
        aggregationLabel: isPercentMetric ? "Median" : "Total",
        isLowerValueBetter: metricConfig.is_lower_value_better,
      }
      return tooltipOpts
    })

    return tooltipOptionsFromChartSettingsAtom
  },
  (a, b) => {
    if (a.length !== b.length) {
      return false
    }

    return (
      dequal(a?.[0]?.metric, b?.[0]?.metric) &&
      dequal(a?.[0]?.display, b?.[0]?.display) &&
      dequal(a?.[0]?.ratioMetric, b?.[0]?.ratioMetric)
    )
  }
)
